/**
 * What the pipeline will accept.
 *
 * Kept in step with `TrackUploads::ACCEPTED` on the server, which is the one
 * that actually decides. This copy exists so somebody is told *before* spending
 * ten minutes uploading a file that would be refused on arrival. It is a
 * courtesy, never the check.
 */
export const ACCEPTED_AUDIO = {
  accept: '.wav,.flac,.aiff,.aif,.mp3,.m4a,.aac,.ogg,audio/*',
  label: 'WAV, FLAC, AIFF, MP3, M4A or OGG',
  types: new Set([
    'audio/wav',
    'audio/x-wav',
    'audio/wave',
    'audio/vnd.wave',
    'audio/flac',
    'audio/x-flac',
    'audio/aiff',
    'audio/x-aiff',
    'audio/mpeg',
    'audio/mp4',
    'audio/x-m4a',
    'audio/aac',
    'audio/ogg',
  ]),
  extensions: new Set(['wav', 'flac', 'aiff', 'aif', 'mp3', 'm4a', 'aac', 'ogg']),
};

/**
 * Type, or failing that, extension.
 *
 * Browsers disagree about audio MIME types. The same AIFF is `audio/aiff` in
 * one and `audio/x-aiff` in another, a FLAC frequently arrives with no type at
 * all, and a file dragged from a network share often has none either. Rejecting
 * on type alone turns a browser quirk into "we do not accept your file", so the
 * extension is the fallback rather than the type being the rule.
 */
export function isAcceptedAudio(file: File): boolean {
  if (file.type && ACCEPTED_AUDIO.types.has(file.type)) return true;

  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

  return ACCEPTED_AUDIO.extensions.has(extension);
}

/**
 * A content type the server will recognise, even when the browser gave none.
 *
 * The signature binds `Content-Type`, so the value sent to `/init` has to be the
 * exact one the browser will later put on the PUT. When the browser has no
 * opinion we supply one from the extension - and then send that same string as
 * the header, which is what keeps the two halves consistent.
 */
const BY_EXTENSION: Record<string, string> = {
  wav: 'audio/wav',
  flac: 'audio/flac',
  aiff: 'audio/aiff',
  aif: 'audio/aiff',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  ogg: 'audio/ogg',
};

export function contentTypeOf(file: File): string {
  if (file.type && ACCEPTED_AUDIO.types.has(file.type)) return file.type;

  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';

  return BY_EXTENSION[extension] ?? 'application/octet-stream';
}

/** "48.2 MB · WAV", for the chosen-file line. */
export function describeFile(file: File): string {
  const mb = file.size / 1_000_000;
  const size = mb >= 1000 ? `${(mb / 1000).toFixed(2)} GB` : `${mb.toFixed(1)} MB`;
  const extension = file.name.split('.').pop()?.toUpperCase() ?? 'audio';

  return `${size} · ${extension}`;
}
